import { useState, useEffect, useRef, } from 'react';
import { useForm, Controller, } from 'react-hook-form'
import styled from 'styled-components';
import Select from 'react-select';
import masks from 'helpers/masks';
import pixKey from 'helpers/pixKey';
import fieldValidations from 'helpers/fieldValidations';
import date from 'helpers/date';
import regex from 'helpers/regex';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Fieldset from 'components/_UI/Fieldset'
import { StyledFieldset } from 'styled/UI/StyledForm';
import Loader from 'components/_UI/Loader';
import Switch from 'components/_UI/Switch';
import { useUser } from 'context/user/User';
import financialApi from 'api/requests/financial';
import eventsApi from 'api/requests/events';
import BankAddressInfo from './BankAddressInfo';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import ConfirmDialog from './ConfirmDialog';
import buttonText from 'texts/buttons/buttonText';
import { Check, User, CreditCard, MapPin, ClipboardCheck, ChevronRight, ChevronLeft, Trash2, Save, AlertCircle, AlertTriangle } from 'lucide-react';

const steps = [
    { number: 1, label: 'Titular', icon: User },
    { number: 2, label: 'Conta', icon: CreditCard },
    { number: 3, label: 'Endereço', icon: MapPin },
    { number: 4, label: 'Revisão', icon: ClipboardCheck },
];

const LAST_STEP = steps.length;

/* O valor de CPF vem cru da API (o setValue não passa pela máscara, só o input
   é que formata na tela). Numa tela de conferência isso importa: a pessoa
   precisa reconhecer o próprio documento para saber se está certo. */
function formatDocument(value) {
    const digits = String(value || '').replace(regex.nonDigit, '');

    if (digits.length === 11) {
        return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }

    if (digits.length === 14) {
        return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }

    return value;
}

function ReviewItem({ label, value, span }) {
    return (
        <ReviewItemWrapper span={span}>
            <ReviewLabel>{label}</ReviewLabel>
            <ReviewValue>{value || '—'}</ReviewValue>
        </ReviewItemWrapper>
    );
}

const bankSelectStyles = {
    control: (base, state) => ({
        ...base,
        background: colors.night + '08',
        borderColor: state.isFocused ? panelAccent(colors.occasionalPurple) : colors.night + '22',
        borderRadius: '10px',
        /* 43px é a altura do input do Fieldset. Sem fixar, o control fica 46px
           e a mensagem de erro da coluna do select nasce 3px abaixo da coluna
           vizinha. */
        minHeight: '43px',
        height: '43px',
        padding: '0 4px',
        fontSize: '14px',
        boxShadow: state.isFocused ? `0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '18')}` : 'none',
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: panelAccentAlpha(colors.occasionalPurple, '66'),
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        border: `1px solid ${colors.night}11`,
        zIndex: 10,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '14px',
        padding: '10px 14px',
        background: state.isSelected
            ? panelAccent(colors.occasionalPurple)
            : state.isFocused ? panelAccentAlpha(colors.occasionalPurple, '0D') : 'transparent',
        color: state.isSelected ? '#fff' : colors.nightDark,
        cursor: 'pointer',
        '&:active': {
            background: panelAccentAlpha(colors.occasionalPurple, '22'),
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: colors.night + '88',
        fontSize: '14px',
    }),
    singleValue: (base) => ({
        ...base,
        color: colors.nightDark,
        fontSize: '14px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? panelAccent(colors.occasionalPurple) : colors.night + '88',
        transition: 'color 0.15s ease',
    }),
};

export default function BankForm({ visible, toast, onClose, closeGuardRef }) {
    const {
        register,
        unregister,
        setValue,
        getValues,
        setError,
        clearErrors,
        handleSubmit,
        control,
        watch,
        reset,
        trigger,
        formState,
        formState: { isDirty, isSubmitting, errors, },
    } = useForm({ mode: 'onChange', });

    const { user, setUser } = useUser();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [accountId, setAccountId] = useState(null);
    const [address, setAddress] = useState({});
    const [banks, setBanks] = useState([]);
    const [cnpj, setCnpj] = useState('');
    const [isCpf, setIsCpf] = useState(true);
    const [isCurrentAccount, setIsCurrentAccount] = useState(true);
    const [isEditing, setIsEditing] = useState(false)
    const [submitHint, setSubmitHint] = useState(null)
    const [requestError, setRequestError] = useState(false);
    const [hasActiveEvents, setHasActiveEvents] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [pixKeyMissingOnLoad, setPixKeyMissingOnLoad] = useState(false);
    const resultError = useRef(null);
    const formAreaRef = useRef(null);

    const watchCep = watch('cep')
    const hasAgencyDigit = watch('hasAgencyDigit')
    const pixType = watch('pix_type')?.value
    const pixKeyValue = watch('pix_key')

    const step1Fields = isCpf ? ['holder', 'cpf', 'responsible_birth_date'] : ['holder', 'cpf', 'cnpj', 'responsible_birth_date'];
    const step2Fields = ['bank', 'agency', 'account', 'digit', 'pix_type', 'pix_key'];
    const step3Fields = ['cep', 'addressDistrict', 'addressStreet', 'addressNumber'];

    const stepFields = { 1: step1Fields, 2: step2Fields, 3: step3Fields };

    /* Some assim que os dois campos são preenchidos: o aviso existe para
       resolver a pendência, não para ficar cobrando quem já resolveu. */
    const pixKeyPending = pixKeyMissingOnLoad && (!pixType || !pixKeyValue);

    function changeRadio() {
        setValue('cpf', getValues('cpf'), { shouldDirty: true })
    }

    /* Trocar o tipo invalida a chave que já estava escrita: um CPF digitado não
       vira telefone. Limpar evita o pior caso, que é a máscara nova reformatar
       os dígitos antigos em algo que passa na validação e paga a pessoa errada. */
    function changePixType() {
        setValue('pix_key', '', { shouldDirty: true, shouldValidate: false })
        clearErrors('pix_key')
    }

    async function goToNext() {
        const fieldsToValidate = stepFields[currentStep] || [];
        const valid = await trigger(fieldsToValidate);
        if (valid) {
            setCurrentStep(prev => Math.min(prev + 1, LAST_STEP));
            return;
        }

        scrollToFirstError();
    }

    /* O rodapé é sticky. Sem rolar até ele, o primeiro erro pode nascer atrás
       do botão que acabou de ser acionado — e o clique parece não ter feito
       nada. O rAF espera o React pintar as mensagens antes de procurá-las. */
    function scrollToFirstError() {
        requestAnimationFrame(() => {
            formAreaRef.current
                ?.querySelector('[data-field-error]')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    function goBack() {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    }

    async function getBanks() {
        try {
            const { data } = await financialApi.getBanks();
            const banks = data.data.map((bank) => ({
                label: `${bank.code} - ${bank.name}`,
                value: bank.id
            }));

            return banks;
        } catch (error) {
            // log error
        }
    }

    async function getAccount() {
        try {
            const response = await financialApi.getBankAccount();

            return response.data;
        } catch (error) {
            // log error
        }
    }

    /* Só é chamada depois do ConfirmDialog: o botão do cabeçalho abre a
       pergunta, nunca a exclusão. Apagar a conta em um clique era irreversível
       e ficava a um deslize do título. */
    async function deleteBankAccount() {
        try {
            setDeleting(true);
            setDeleteError(null);
            await financialApi.deleteBankAccount(accountId);

            resetForm();
            setAccountId(null);
            setConfirmDelete(false);
            onClose();

            toast.success('Conta deletada com sucesso');
        } catch (error) {
            // log error
            /* O erro fica no próprio diálogo: fechá-lo para mostrar a mensagem
               atrás obrigaria o produtor a recomeçar para tentar de novo. */
            setDeleteError('Não foi possível excluir a conta. Tente novamente.');
        } finally {
            setDeleting(false);
        }
    }

    async function editBankAccount(data) {
        const { holder, digit, account, agency, agencyDigit, hasAgencyDigit, cpf, bank, responsible_birth_date } = data;
        try {
            setLoading(true);
            setIsEditing(true);
            setRequestError(false);
            const payload = {
                holder: holder,
                agency: agency.replaceAll(regex.nonDigit, ''),
                agency_digit: hasAgencyDigit ? (agencyDigit || '') : '',
                account: `${account}${digit}`,
                account_type: isCurrentAccount ? 'corrente' : 'poupanca',
                cpf_cnpj: isCpf ? 'cpf' : 'cnpj',
                cpf: cpf.replaceAll(regex.nonDigit, ''),
                bank_id: bank.value,
                pix_type: data.pix_type.value,
                pix_key: pixKey.normalize(data.pix_type.value, data.pix_key),
                responsible_birth_date: date.unformat(responsible_birth_date),
                address: {
                    postalcode: data.cep,
                    number: String(data.addressNumber),
                }
            };

            if (!isCpf) {
                payload.cnpj = data.cnpj.replaceAll(regex.nonDigit, '');
            }

            await financialApi.editBankAccount(accountId, payload);

            onClose();

            toast.success('Conta editada com sucesso');
        } catch (error) {
            // log error
            setRequestError('Ocorreu um erro inesperado!')
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultError?.current?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }
    }

    async function createBankAccount(data) {
        const { holder, digit, account, agency, agencyDigit, hasAgencyDigit, cpf, bank, cep, addressNumber, responsible_birth_date } = data;
        try {
            setLoading(true);
            setRequestError(false);
            const payload = {
                holder: holder,
                agency: agency.replaceAll(regex.nonDigit, ''),
                agency_digit: hasAgencyDigit ? (agencyDigit || '') : '',
                account: `${account}${digit}`,
                account_type: isCurrentAccount ? 'corrente' : 'poupanca',
                cpf_cnpj: isCpf ? 'cpf' : 'cnpj',
                cpf: cpf.replaceAll(regex.nonDigit, ''),
                bank_id: bank.value,
                pix_type: data.pix_type.value,
                pix_key: pixKey.normalize(data.pix_type.value, data.pix_key),
                responsible_birth_date: date.unformat(responsible_birth_date),
                address: {
                    postalcode: cep,
                    number: String(addressNumber),
                }
            };
            if (!isCpf) {
                payload.cnpj = data.cnpj.replaceAll(regex.nonDigit, '');
            }

            const { data: accounting } = await financialApi.registerBankAccount(payload);

            setAccountId(accounting.id);
            refetchUser();

            onClose();

            toast.success('Conta criada com sucesso');
        } catch (error) {
            setRequestError('Ocorreu um erro inesperado!');
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultError?.current?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }
    }

    function refetchUser() {
        setUser({
            ...user,
            has_bank_account: true
        });
    }

    function resetForm() {
        reset({
            holder: '',
            cpf: '',
            responsible_birth_date: '',
            bank: null,
            agency: '',
            agencyDigit: '',
            hasAgencyDigit: false,
            account: '',
            digit: '',
            cnpj: '',
            pix_type: null,
            pix_key: '',
            cep: '',
            addressNumber: '',
            addressDistrict: '',
            addressStreet: '',
        })
        setAccountId(null)
    }

    async function setInitialData() {
        try {
            setLoading(true);
            const banks = await getBanks();
            setBanks(banks);

            const { data: accounting } = await getAccount();
            const { data: myEvents } = await eventsApi.myEvents();

            if (myEvents?.data?.now?.total > 0 || myEvents?.data?.next?.total > 0) {
                setHasActiveEvents(true);
            }

            if (!accounting || !accounting.id) {
                return;
            }

            setIsEditing(true);

            const account = accounting.account.slice(0, -1);
            const digit = accounting.account.slice(-1);

            setAccountId(accounting.id);
            setAddress(accounting.address);
            setValue('holder', accounting.holder);
            if (accounting.responsible_birth_date) {
                setValue('responsible_birth_date', date.format(accounting.responsible_birth_date));
            }
            setValue('account', account);
            setValue('digit', digit);
            setValue('agency', accounting.agency);
            setValue('agencyDigit', accounting.agency_digit || '');
            setValue('hasAgencyDigit', !!accounting.agency_digit);
            setValue('cpf', accounting.cpf);

            if (accounting.cnpj) {
                setValue('cnpj', accounting.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5"));
            }

            setIsCpf(accounting.cpf_cnpj === 'cpf');
            setIsCurrentAccount(accounting.account_type === 'corrente');

            const userBank = banks.find(bank => bank.value === accounting.bank_id);
            setValue('bank', userBank);

            /* Conta criada antes de a chave Pix existir vem sem os dois campos.
               Não é erro: o produtor precisa preenchê-los, e a API recusa o
               saque até lá — por isso o aviso no topo, e não só o erro que
               apareceria lá no passo 2. */
            setValue('pix_type', pixKey.optionFor(accounting.pix_type));
            setValue('pix_key', pixKey.toInput(accounting.pix_type, accounting.pix_key));
            setPixKeyMissingOnLoad(!accounting.pix_type || !accounting.pix_key);

            setValue('cep', accounting.address.street.postalcode);
            setValue('addressNumber', accounting.address.number);
            setValue('addressDistrict', accounting.address.street.district);
            setValue('addressStreet', accounting.address.street.name);
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (visible) {
            setCurrentStep(1);
            setInitialData();
        }
    }, [visible]);

    useEffect(() => {
        if (isCpf) {
            setCnpj(getValues('cnpj'))
            unregister('cnpj')
        } else {
            register('cnpj')

            if (!getValues('cnpj')) {
                setValue('cnpj', cnpj)
            }
        }
    }, [isCpf])

    /* O Modal fecha no clique do backdrop e no "x", e um deles é fácil de
       acertar sem querer. São três passos de dados bancários: sumir com tudo
       sem perguntar custa caro demais para o produtor refazer. */
    useEffect(() => {
        if (!closeGuardRef) {
            return;
        }

        closeGuardRef.current = () => {
            if (!isDirty || isSubmitting) {
                return true;
            }

            setConfirmDiscard(true);
            return false;
        };

        return () => {
            closeGuardRef.current = null;
        };
    }, [closeGuardRef, isDirty, isSubmitting]);

    function discardAndClose() {
        setConfirmDiscard(false);
        onClose();
    }

    /* O botão de salvar não fica mais cinza e mudo: ele sempre responde. Sem
       alterações, diz que não há o que salvar; com campo inválido, leva até o
       passo onde está o erro. Esperar que a pessoa adivinhe por que o botão
       está desabilitado era o pior retorno possível. */
    function submitAccount() {
        if (!isDirty) {
            setSubmitHint('Nenhuma alteração para salvar.');
            return;
        }

        setSubmitHint(null);
        handleSubmit(accountId ? editBankAccount : createBankAccount, onInvalidSubmit)();
    }

    function onInvalidSubmit(formErrors) {
        const firstField = Object.keys(formErrors)[0];

        if (!firstField) {
            return;
        }

        setSubmitHint(null);
        setCurrentStep(stepOfField(firstField));
        scrollToFirstError();
    }

    function stepOfField(field) {
        if (step1Fields.includes(field)) return 1;
        if (step2Fields.includes(field)) return 2;
        if (step3Fields.includes(field)) return 3;

        return LAST_STEP;
    }

    /* O Enter dentro de qualquer campo cai aqui: avança enquanto houver passo,
       salva na revisão. Antes o Enter simplesmente não fazia nada. */
    function handleFormSubmit(event) {
        event.preventDefault();

        if (currentStep < LAST_STEP) {
            goToNext();
            return;
        }

        submitAccount();
    }

    /* Só a tela de revisão lê os valores brutos, e ela não tem campo nenhum:
       enquanto estiver aberta nada muda por baixo, então getValues() basta. */
    const review = currentStep === LAST_STEP ? getValues() : null;

    function reviewAgency() {
        return review.hasAgencyDigit && review.agencyDigit
            ? `${review.agency}-${review.agencyDigit}`
            : review.agency;
    }

    return (
        <ModalWrapper>
            <ModalHeader>
                <HeaderLeft>
                    <TitleText>Conta bancária</TitleText>
                    {accountId && <EditingBadge>Editando</EditingBadge>}
                </HeaderLeft>
                {accountId && !hasActiveEvents && (
                    <DeleteButton
                        onClick={() => { setDeleteError(null); setConfirmDelete(true); }}
                        disabled={isSubmitting}
                        title='Deletar conta'
                    >
                        <Trash2 size={18} />
                        <span>Deletar</span>
                    </DeleteButton>
                )}
            </ModalHeader>

            {loading ?
                <Loader /> :
                <>
                    <StepperContainer>
                        {steps.map((step, index) => {
                            const StepIcon = step.icon;
                            const isActive = currentStep === step.number;
                            const isCompleted = currentStep > step.number;

                            return (
                                <StepperItem key={step.number}>
                                    <StepCircle
                                        active={isActive}
                                        completed={isCompleted}
                                        onClick={() => {
                                            if (isCompleted) setCurrentStep(step.number);
                                        }}
                                        clickable={isCompleted}
                                    >
                                        {isCompleted
                                            ? <Check size={14} color='#fff' />
                                            : <StepIcon size={14} color={isActive ? '#fff' : colors.night + '88'} />
                                        }
                                    </StepCircle>
                                    <StepLabel active={isActive} completed={isCompleted}>
                                        {step.label}
                                    </StepLabel>
                                    {index < steps.length - 1 && (
                                        <StepLine completed={currentStep > step.number} />
                                    )}
                                </StepperItem>
                            );
                        })}
                    </StepperContainer>

                    {pixKeyPending && currentStep !== 2 && (
                        <PendingPixBanner>
                            <AlertTriangle size={17} />
                            <span>
                                Esta conta foi cadastrada antes da chave Pix e está sem ela.
                                <strong> Enquanto faltar, o saque não é liberado.</strong>
                            </span>
                            <PendingPixAction onClick={() => setCurrentStep(2)}>
                                Preencher agora
                            </PendingPixAction>
                        </PendingPixBanner>
                    )}

                    <StepForm onSubmit={handleFormSubmit}>
                    <ModernFormArea ref={formAreaRef}>
                        {/* Step 1 - Titular */}
                        {currentStep === 1 && (
                            <StepContent>
                                <StepDescription>
                                    Informe os dados do titular da conta bancária.
                                </StepDescription>
                                <RequiredHint>* Campos obrigatórios</RequiredHint>

                                <FieldGrid columns={2}>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Nome do titular'
                                            label='Nome do titular *'
                                            layout='squired'
                                            name='holder'
                                            register={register}
                                            validations={fieldValidations.generic('Titular')}
                                        />
                                        {errors?.holder?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.holder?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>

                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='CPF'
                                            label='CPF *'
                                            layout='squired'
                                            name='cpf'
                                            mask={masks.cpf}
                                            register={register}
                                            validations={fieldValidations.cpf('CPF')}
                                        />
                                        {errors?.cpf?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.cpf?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>

                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='DD/MM/AAAA'
                                            label='Data de nascimento do responsável *'
                                            layout='squired'
                                            name='responsible_birth_date'
                                            mask={masks.date}
                                            register={register}
                                            validations={{
                                                required: 'Preencha a data de nascimento',
                                                pattern: {
                                                    value: /^\d{2}\/\d{2}\/\d{4}$/,
                                                    message: 'Data inválida'
                                                },
                                                validate: {
                                                    validBirthDate: (value) => {
                                                        const parts = value.split('/');
                                                        const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                                                        if (isNaN(d.getTime())) return 'Data inválida';
                                                        if (d >= new Date()) return 'A data deve ser no passado';
                                                        const minDate = new Date();
                                                        minDate.setFullYear(minDate.getFullYear() - 120);
                                                        if (d < minDate) return 'Data inválida';
                                                        return true;
                                                    }
                                                }
                                            }}
                                        />
                                        {errors?.responsible_birth_date?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.responsible_birth_date?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <SegmentedContainer>
                                    <SegmentedLabel>Tipo de entidade</SegmentedLabel>
                                    <SegmentedRow>
                                        <SegmentedButton
                                            active={isCpf}
                                            onClick={() => { setIsCpf(true); changeRadio() }}
                                        >
                                            Pessoa Física
                                        </SegmentedButton>
                                        <SegmentedButton
                                            active={!isCpf}
                                            onClick={() => { setIsCpf(false); changeRadio() }}
                                        >
                                            Pessoa Jurídica
                                        </SegmentedButton>
                                    </SegmentedRow>
                                </SegmentedContainer>

                                {!isCpf && (
                                    <FieldGrid columns={1} style={{ marginTop: 12 }}>
                                        <FieldWrapper half>
                                            <Fieldset
                                                placeholder='CNPJ'
                                                label='CNPJ *'
                                                layout='squired'
                                                name='cnpj'
                                                mask={masks.cnpj}
                                                register={register}
                                                validations={fieldValidations.cnpj('CNPJ', !isCpf)}
                                            />
                                            {errors?.cnpj?.message && (
                                                <FieldError>
                                                    <AlertCircle size={13} />
                                                    {errors.cnpj?.message}
                                                </FieldError>
                                            )}
                                        </FieldWrapper>
                                    </FieldGrid>
                                )}
                            </StepContent>
                        )}

                        {/* Step 2 - Conta */}
                        {currentStep === 2 && (
                            <StepContent>
                                <StepDescription>
                                    Selecione seu banco e informe os dados da conta.
                                </StepDescription>
                                <RequiredHint>* Campos obrigatórios</RequiredHint>

                                <FieldGrid columns={1}>
                                    <FieldWrapper>
                                        <SelectLabel>Banco <RequiredMark>*</RequiredMark></SelectLabel>
                                        <Controller
                                            control={control}
                                            name='bank'
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <Select
                                                    placeholder='Selecione seu banco...'
                                                    options={banks}
                                                    styles={bankSelectStyles}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    ref={field.ref}
                                                />
                                            )}
                                        />
                                        {errors?.bank && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                Selecione um banco
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <FieldGrid columns={4}>
                                    <FieldWrapper span={hasAgencyDigit ? 1 : 2}>
                                        <Fieldset
                                            placeholder='Agência'
                                            label='Agência *'
                                            layout='squired'
                                            name='agency'
                                            register={register}
                                            validations={fieldValidations.bankAgency()}
                                        />
                                        {errors?.agency?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.agency?.message}
                                            </FieldError>
                                        )}
                                        <Controller
                                            control={control}
                                            name='hasAgencyDigit'
                                            render={({ field }) => (
                                                <Switch
                                                    id='hasAgencyDigitSwitch'
                                                    label='Adicionar dígito da agência'
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    marginTop='8px'
                                                    labelColor={colors.nightDark}
                                                    labelSize='13px'
                                                />
                                            )}
                                        />
                                    </FieldWrapper>
                                    {hasAgencyDigit && (
                                        <FieldWrapper>
                                            <Fieldset
                                                placeholder='Dígito'
                                                label='Dígito Ag.'
                                                layout='squired'
                                                name='agencyDigit'
                                                register={register}
                                                validations={{ 
                                                    maxLength: { value: 1, message: 'Máximo 1 dígito' } 
                                                }}
                                                maxLength={1}
                                            />
                                        </FieldWrapper>
                                    )}
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Conta'
                                            label='Conta *'
                                            layout='squired'
                                            name='account'
                                            maskPlaceholder=''
                                            masklabel=''
                                            register={register}
                                            validations={fieldValidations.bankAccount()}
                                        />
                                        {errors?.account?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.account?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Dígito'
                                            label='Dígito *'
                                            layout='squired'
                                            name='digit'
                                            register={register}
                                            validations={fieldValidations.bankDigit()}
                                        />
                                        {errors?.digit?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.digit?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <SegmentedContainer>
                                    <SegmentedLabel>Tipo de conta</SegmentedLabel>
                                    <SegmentedRow>
                                        <SegmentedButton
                                            active={isCurrentAccount}
                                            onClick={() => { setIsCurrentAccount(true); changeRadio() }}
                                        >
                                            Corrente
                                        </SegmentedButton>
                                        <SegmentedButton
                                            active={!isCurrentAccount}
                                            onClick={() => { setIsCurrentAccount(false); changeRadio() }}
                                        >
                                            Poupança
                                        </SegmentedButton>
                                    </SegmentedRow>
                                </SegmentedContainer>

                                <PixNotice>
                                    <AlertCircle size={15} />
                                    <span>
                                        O saque é pago por Pix nesta chave. Confira com atenção:
                                        é para ela que o dinheiro vai.
                                    </span>
                                </PixNotice>

                                <FieldGrid columns={2}>
                                    <FieldWrapper>
                                        <SelectLabel>Tipo da chave Pix <RequiredMark>*</RequiredMark></SelectLabel>
                                        <Controller
                                            control={control}
                                            name='pix_type'
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <Select
                                                    placeholder='Selecione...'
                                                    options={pixKey.options}
                                                    styles={bankSelectStyles}
                                                    value={field.value}
                                                    onChange={(option) => {
                                                        field.onChange(option);
                                                        changePixType();
                                                    }}
                                                    onBlur={field.onBlur}
                                                    ref={field.ref}
                                                />
                                            )}
                                        />
                                        {errors?.pix_type && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                Selecione o tipo da chave
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Fieldset
                                            /* A máscara muda com o tipo, e o react-input-mask não
                                               reaplica sozinho no valor que já está no input — a key
                                               força a remontagem do campo a cada troca de tipo. */
                                            key={pixType || 'pix-key-empty'}
                                            placeholder={pixKey.placeholderFor(pixType)}
                                            label='Chave Pix *'
                                            layout='squired'
                                            name='pix_key'
                                            mask={pixKey.maskFor(pixType)}
                                            maskPlaceholder=''
                                            disabled={!pixType}
                                            register={register}
                                            validations={fieldValidations.pixKey(pixType)}
                                        />
                                        {/* Sem tipo escolhido o campo está desabilitado, e um erro
                                            "campo obrigatório" apontaria para algo em que não dá
                                            para clicar. O erro do select ao lado já diz o que fazer. */}
                                        {pixType && errors?.pix_key?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.pix_key?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>
                            </StepContent>
                        )}

                        {/* Step 3 - Endereço */}
                        {currentStep === 3 && (
                            <StepContent>
                                <StepDescription>
                                    Informe o endereço vinculado à conta bancária.
                                </StepDescription>
                                <RequiredHint>* Campos obrigatórios</RequiredHint>

                                <BankAddressInfo
                                    register={register}
                                    setValue={setValue}
                                    getValues={getValues}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    errors={errors}
                                    isEditing={isEditing}
                                    setIsEditing={setIsEditing}
                                    watchCep={watchCep}
                                />
                            </StepContent>
                        )}

                        {/* Step 4 - Revisão */}
                        {currentStep === LAST_STEP && review && (
                            <StepContent>
                                <StepDescription>
                                    Confira os dados antes de salvar.
                                </StepDescription>
                                <RequiredHint>
                                    É para a chave Pix abaixo que o dinheiro dos seus saques vai.
                                </RequiredHint>

                                <ReviewGroup>
                                    <ReviewGroupHeader>
                                        <ReviewGroupTitle>Titular</ReviewGroupTitle>
                                        <EditStepButton onClick={() => setCurrentStep(1)}>
                                            Editar
                                        </EditStepButton>
                                    </ReviewGroupHeader>
                                    <ReviewGrid>
                                        <ReviewItem label='Nome do titular' value={review.holder} span={2} />
                                        <ReviewItem label='Tipo de entidade' value={isCpf ? 'Pessoa Física' : 'Pessoa Jurídica'} />
                                        <ReviewItem label='CPF' value={formatDocument(review.cpf)} />
                                        {!isCpf && <ReviewItem label='CNPJ' value={formatDocument(review.cnpj)} />}
                                        <ReviewItem label='Nascimento do responsável' value={review.responsible_birth_date} />
                                    </ReviewGrid>
                                </ReviewGroup>

                                <ReviewGroup>
                                    <ReviewGroupHeader>
                                        <ReviewGroupTitle>Conta</ReviewGroupTitle>
                                        <EditStepButton onClick={() => setCurrentStep(2)}>
                                            Editar
                                        </EditStepButton>
                                    </ReviewGroupHeader>
                                    <ReviewGrid>
                                        <ReviewItem label='Banco' value={review.bank?.label} span={2} />
                                        <ReviewItem label='Agência' value={reviewAgency()} />
                                        <ReviewItem label='Conta' value={`${review.account}-${review.digit}`} />
                                        <ReviewItem label='Tipo de conta' value={isCurrentAccount ? 'Corrente' : 'Poupança'} />
                                    </ReviewGrid>

                                    {/* A chave Pix sai do bloco comum de propósito: é o único
                                        dado aqui em que um erro paga outra pessoa. */}
                                    <ReviewPixHighlight>
                                        <ReviewGrid>
                                            <ReviewItem label='Tipo da chave Pix' value={review.pix_type?.label} />
                                            <ReviewItem label='Chave Pix' value={review.pix_key} />
                                        </ReviewGrid>
                                    </ReviewPixHighlight>
                                </ReviewGroup>

                                <ReviewGroup>
                                    <ReviewGroupHeader>
                                        <ReviewGroupTitle>Endereço</ReviewGroupTitle>
                                        <EditStepButton onClick={() => setCurrentStep(3)}>
                                            Editar
                                        </EditStepButton>
                                    </ReviewGroupHeader>
                                    <ReviewGrid>
                                        <ReviewItem label='CEP' value={review.cep} />
                                        <ReviewItem label='Bairro' value={review.addressDistrict} />
                                        <ReviewItem label='Rua' value={review.addressStreet} />
                                        <ReviewItem label='Número' value={review.addressNumber} />
                                    </ReviewGrid>
                                </ReviewGroup>

                                {requestError && (
                                    <RequestError ref={resultError}>
                                        <AlertCircle size={16} />
                                        {requestError}
                                    </RequestError>
                                )}
                            </StepContent>
                        )}
                    </ModernFormArea>

                    <Footer>
                        <FooterLeft>
                            {currentStep > 1 && (
                                <BackButton onClick={goBack}>
                                    <ChevronLeft size={18} />
                                    Voltar
                                </BackButton>
                            )}
                        </FooterLeft>

                        <FooterRight>
                            {currentStep < LAST_STEP ? (
                                <NextButton>
                                    {currentStep === LAST_STEP - 1 ? 'Revisar' : 'Próximo'}
                                    <ChevronRight size={18} />
                                </NextButton>
                            ) : (
                                <>
                                    {/* Some sozinho no instante em que houver o que salvar. */}
                                    {submitHint && !isDirty && (
                                        <SubmitHint role='status'>{submitHint}</SubmitHint>
                                    )}
                                    <SubmitButton disabled={isSubmitting}>
                                        <Save size={18} />
                                        {accountId ? 'Atualizar conta' : 'Criar conta'}
                                    </SubmitButton>
                                </>
                            )}
                        </FooterRight>
                    </Footer>
                    </StepForm>
                </>
            }

            <ConfirmDialog
                visible={confirmDiscard}
                title='Descartar alterações?'
                description='O que você preencheu nesta conta bancária ainda não foi salvo e será perdido.'
                confirmLabel='Descartar'
                cancelLabel='Continuar editando'
                onConfirm={discardAndClose}
                onCancel={() => setConfirmDiscard(false)}
            />

            <ConfirmDialog
                visible={confirmDelete}
                title='Excluir conta bancária?'
                description='Sem uma conta cadastrada você não consegue solicitar saques. Para voltar a receber, será preciso cadastrar tudo de novo.'
                confirmLabel={buttonText.delete}
                loading={deleting}
                error={deleteError}
                onConfirm={deleteBankAccount}
                onCancel={() => setConfirmDelete(false)}
            />
        </ModalWrapper>
    )
}

/* ============================
   Layout
   ============================ */

const ModalWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;

    /* O Modal centraliza o conteúdo por padrão (Modal.defaultProps.align =
       'center'). Só o StyledFieldset se defende disso com text-align próprio,
       então label de select, descrição do passo e até as opções do dropdown
       ficavam centralizadas no meio de um formulário alinhado à esquerda. */
    text-align: left;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${colors.night}11;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleText = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    margin: 0;
`;

const EditingBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    background: ${panelAccentAlpha(colors.occasionalPurple, '14')};
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '33')};
    padding: 3px 10px;
    border-radius: 20px;
`;

/* Os botões deste formulário compartilham a mesma altura mínima: abaixo de
   1200px o modal ocupa a tela inteira, então todos são alvos de toque. */
const buttonHeight = `
    min-height: 44px;
    justify-content: center;
`;

const DeleteButton = styled.button.attrs({ type: 'button' })`
    ${buttonHeight};
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid ${colors.danger}44;
    border-radius: 8px;
    background: transparent;
    color: ${colors.danger};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background: ${colors.danger}0D;
        border-color: ${colors.danger};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    & > span {
        @media screen and (max-width: ${breakpoints.sm}) {
            display: none;
        }
    }
`;

/* ============================
   Stepper
   ============================ */

const StepperContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 28px;
    padding: 0 10px;
`;

const StepperItem = styled.div`
    display: flex;
    align-items: center;
`;

const StepCircle = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
    cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
    background: ${({ active, completed }) =>
        active ? panelAccent(colors.occasionalPurple)
        : completed ? panelAccent(colors.occasionalPurple)
        : colors.night + '0D'};
    border: 2px solid ${({ active, completed }) =>
        active || completed ? panelAccent(colors.occasionalPurple) : colors.night + '22'};

    ${({ clickable }) => clickable && `
        &:hover {
            transform: scale(1.08);
        }
    `}
`;

const StepLabel = styled.span`
    font-size: 13px;
    font-weight: ${({ active }) => active ? '700' : '400'};
    margin-left: 6px;
    white-space: nowrap;
    color: ${({ active, completed }) =>
        active || completed ? panelAccent(colors.occasionalPurple) : colors.night + '88'};
`;

const StepLine = styled.div`
    width: 40px;
    height: 2px;
    margin: 0 10px;
    flex-shrink: 0;
    border-radius: 1px;
    background: ${({ completed }) => completed ? panelAccent(colors.occasionalPurple) : colors.night + '18'};
    transition: background 0.2s ease;

    @media screen and (max-width: ${breakpoints.sm}) {
        width: 20px;
        margin: 0 6px;
    }
`;

/* ============================
   Modern form area
   Overrides all nested Fieldset
   input/label styles via cascade
   ============================ */

/* O <form> existe para o Enter ter comportamento nativo: sem ele, Enter em
   qualquer campo não fazia nada. Mantém display:flex para não alterar o
   empilhamento que o ModalWrapper já tinha. */
const StepForm = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
`;

const ModernFormArea = styled.div`
    flex: 1;
    width: 100%;

    /* Override label styles.
       O gap label -> campo mora só aqui: a margem do label é a distância
       final, e o input não acrescenta margin-top própria. Assim a coluna do
       Fieldset e a coluna do react-select (que não tem essa margem) começam
       na mesma linha. */
    label {
        font-size: 13px;
        font-weight: 600;
        color: ${colors.nightDark};
        margin-bottom: 8px;
        display: block;
    }

    /* O Fieldset traz padding-bottom: 8px por defaultProp. Aqui o respiro
       entre campos vem do gap do grid, e esse padding só empurrava a mensagem
       de erro 8px abaixo da mensagem da coluna vizinha. */
    ${StyledFieldset} {
        padding-bottom: 0;
    }

    /* Override squired inputs but not react-select internals */
    input:not([class*="css-"]):not([id*="react-select"]) {
        background: ${colors.night}08 !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 10px !important;
        padding: 12px 14px !important;
        font-size: 14px !important;
        margin-top: 0 !important;
        transition: all 0.15s ease !important;
        color: ${colors.nightDark} !important;

        &::placeholder {
            color: ${colors.night}77 !important;
            font-weight: 400 !important;
        }

        &:focus {
            border-color: ${panelAccent(colors.occasionalPurple)} !important;
            box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '18')} !important;
            background: #fff !important;
        }
    }
`;

const StepContent = styled.div`
    width: 100%;
`;

const StepDescription = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0 0 8px 0;
    line-height: 1.5;
`;

const RequiredHint = styled.span`
    display: block;
    font-size: 12px;
    color: ${colors.night}99;
    margin-bottom: 16px;
`;

const RequiredMark = styled.span`
    color: ${panelAccent(colors.occasionalPurple)};
    font-weight: 600;
`;

/* ============================
   Field grid & wrappers
   ============================ */

const FieldGrid = styled.div`
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    margin-bottom: 8px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(${({ columns }) => columns || 1}, 1fr);
    }
`;

const FieldWrapper = styled.div`
    width: 100%;

    ${({ half }) => half && `
        @media screen and (min-width: ${breakpoints.md}) {
            max-width: 50%;
        }
    `}

    ${({ span }) => span && `
        @media screen and (min-width: ${breakpoints.md}) {
            grid-column: span ${span};
        }
    `}
`;

const SelectLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 8px;
    display: block;
`;

/* O data-attribute marca todos os erros de uma vez: goToNext usa o primeiro
   deles para rolar a tela até o campo que barrou o avanço. O role='alert' faz
   o leitor de tela anunciar a mensagem quando ela aparece — sem isso, quem não
   enxerga a tela clica em "Próximo" e não recebe retorno nenhum. */
const FieldError = styled.span.attrs({ 'data-field-error': '', role: 'alert' })`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 8px;
    padding: 4px 8px;
    background: ${colors.danger}0A;
    border-radius: 6px;
    line-height: 1.4;
`;

/* O aviso é deliberadamente destacado: a chave Pix é o único campo do formulário
   em que um erro de digitação manda dinheiro para outra pessoa. Os dados
   bancários acima só falham para a pessoa certa. */
const PixNotice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 24px;
    margin-bottom: 12px;
    padding: 10px 12px;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${colors.nightDark};
    background: ${colors.occasionalPurple}0F;
    border-left: 3px solid ${colors.occasionalPurple};
    border-radius: 6px;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
        color: ${colors.occasionalPurple};
    }
`;

/* ============================
   Aviso de chave Pix pendente
   ============================ */

const PendingPixBanner = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${colors.warning};
    border: 1px solid ${colors.occasionalOrange}55;
    color: ${colors.nightDark};
    font-size: 13px;
    line-height: 1.45;

    svg {
        flex-shrink: 0;
        color: ${colors.occasionalOrange};
    }

    span {
        flex: 1;
        min-width: 200px;
    }
`;

const PendingPixAction = styled.button.attrs({ type: 'button' })`
    min-height: 36px;
    padding: 6px 14px;
    border: 1px solid ${colors.nightDark}33;
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
        border-color: ${colors.nightDark};
    }
`;

/* ============================
   Revisão
   ============================ */

const ReviewGroup = styled.div`
    border: 1px solid ${colors.night}18;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
`;

const ReviewGroupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${colors.night}11;
`;

const ReviewGroupTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.night};
`;

/* 36px em vez dos 44px dos botões de ação: um "Editar" de 44px domina o
   cabeçalho do bloco que ele só acompanha. Segue acima do mínimo de 24px. */
const EditStepButton = styled.button.attrs({ type: 'button' })`
    min-height: 36px;
    padding: 6px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple}0F;
    }
`;

const ReviewGrid = styled.div`
    display: grid;
    gap: 12px 24px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const ReviewItemWrapper = styled.div`
    min-width: 0;

    ${({ span }) => span && `
        @media screen and (min-width: ${breakpoints.md}) {
            grid-column: span ${span};
        }
    `}
`;

const ReviewLabel = styled.span`
    display: block;
    font-size: 12px;
    color: ${colors.night};
    margin-bottom: 2px;
`;

const ReviewValue = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow-wrap: anywhere;
`;

const ReviewPixHighlight = styled.div`
    margin-top: 14px;
    padding: 10px 12px;
    border-left: 3px solid ${colors.occasionalPurple};
    border-radius: 6px;
    background: ${colors.occasionalPurple}0F;
`;

const RequestError = styled.div.attrs({ role: 'alert' })`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 10px;
    background: ${colors.danger}0D;
    border: 1px solid ${colors.danger}22;
    color: ${colors.danger};
    font-size: 14px;
    font-weight: 500;
`;

/* ============================
   Segmented control
   ============================ */

const SegmentedContainer = styled.div`
    margin-top: 20px;
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 50%;
    }
`;

const SegmentedLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 8px;
    display: block;
`;

const SegmentedRow = styled.div`
    display: flex;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.night}18;
    background: ${colors.night}08;
`;

const SegmentedButton = styled.button.attrs({ type: 'button' })`
    ${buttonHeight}
    flex: 1;
    padding: 10px 16px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.15s ease;
    background: ${({ active }) => active ? panelAccent(colors.occasionalPurple) : 'transparent'};
    color: ${({ active }) => active ? '#fff' : colors.night + 'AA'};

    &:hover {
        background: ${({ active }) => active ? panelAccent(colors.occasionalPurple) : panelAccentAlpha(colors.occasionalPurple, '0D')};
        color: ${({ active }) => active ? '#fff' : panelAccent(colors.occasionalPurple)};
    }
`;

/* ============================
   Footer / navigation
   ============================ */

/* O passo 2 é mais alto que a área visível do modal, e sem isto o botão que
   avança/salva nascia abaixo da dobra: era preciso rolar para descobrir que
   existe uma ação. Grudado no rodapé, ele está sempre à vista. */
const Footer = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 28px;
    padding: 16px 0;
    border-top: 1px solid ${colors.night}11;
    background: ${colors.neutralLight};
    width: 100%;
`;

const FooterLeft = styled.div`
    display: flex;
`;

const FooterRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    margin-left: auto;
`;

const SubmitHint = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const BackButton = styled.button.attrs({ type: 'button' })`
    ${buttonHeight}
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 18px;
    border: 1px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const NextButton = styled.button.attrs({ type: 'submit' })`
    ${buttonHeight}
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.secondaryPurple)};
        transform: translateX(2px);
    }
`;

const SubmitButton = styled.button.attrs({ type: 'submit' })`
    ${buttonHeight}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : panelAccent(colors.occasionalPurple)};
    color: ${({ disabled }) => disabled ? colors.night + '66' : '#fff'};
    font-size: 14px;
    font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background: ${panelAccent(colors.secondaryPurple)};
    }
`;
